import { ReferenceType } from '../../../../../../../../../../../backend/src/types/types';
import type { KimChronologyFactDetail } from '@/api/queries/useGetFactDetailsForKimChronology';
import { Chip } from '@/new/components/chip';
import { Icon } from '@/new/components/icon';
import { ReferencePreviewPopover } from '@/new/components/reference-preview-popover';
import { Skeleton } from '@/new/components/shadcn/skeleton';
import { CHIP_COLOURS } from '@/new/lib/chip-colours';

export interface KimChronologyFactProps {
  factId: string;
  summary: KimChronologyFactDetail | undefined;
  isLoading: boolean;
  referenceIndexMap: Map<string, { index: number; referenceType: ReferenceType }>;
  onReferenceClick?: (id: string, referenceType: ReferenceType) => void;
}

export function KimChronologyFact({
  factId,
  summary,
  isLoading,
  referenceIndexMap,
  onReferenceClick,
}: KimChronologyFactProps) {
  if (isLoading) {
    return (
      <div className="flex flex-1 flex-col gap-2 px-4 py-3">
        <Skeleton className="h-4 w-3/4" />
        <Skeleton className="h-4 w-1/2" />
      </div>
    );
  }

  if (!summary) return null;

  const reference = referenceIndexMap.get(factId);

  return (
    <button
      className="group min-w-0 flex-1 cursor-pointer rounded-xl border bg-white px-4 py-3 pt-2.75 pr-3 text-left font-sans text-[13px] ring-offset-2 transition-colors duration-150 hover:border-gray-300 focus-visible:ring-1 focus-visible:ring-teal-500 focus-visible:outline-none"
      onClick={() => onReferenceClick?.(factId, ReferenceType.Fact)}
    >
      <div className="mr-4 w-full text-gray-800">
        <div className="mb-1.5 flex items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            {reference && (
              <ReferencePreviewPopover id={factId} referenceType={ReferenceType.Fact}>
                <span className="rounded-md border border-blue-300 bg-blue-50 px-1.25 py-0.25 text-[11px] text-blue-500">
                  {reference.index}
                </span>
              </ReferencePreviewPopover>
            )}
            <p className="flex items-center gap-2 font-semibold break-all text-gray-700">
              <span className="-mb-0.25 line-clamp-1">{summary.factDateText ?? '(Date Unknown)'}</span>
            </p>
          </div>

          <div className="flex min-w-0 items-center gap-1.5 overflow-x-auto [scrollbar-width:none]">
            {summary.sources.map((source) => (
              <div key={source.docId} className="flex items-center gap-1.5">
                <p className="flex max-w-56 cursor-pointer items-center gap-1.25 text-xs break-all text-ellipsis text-blue-600 duration-150 hover:text-blue-700">
                  <Icon className="lucide--file mb-0.25" />
                  <span className="line-clamp-1">{source.fileName}</span>
                </p>
                <Chip className={CHIP_COLOURS.type}>{source.docType}</Chip>
              </div>
            ))}
          </div>
        </div>

        <p className="text-gray-800">{summary.description}</p>
      </div>
    </button>
  );
}
